import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Conversation, ConversationContact } from 'app/models/conversation.model';
import { ConversationService } from 'app/services/conversation.service';
import { AutoTranslateDirective } from 'app/shared/directives/auto-translate.directive';
import { AutoTranslatePipe } from 'app/shared/pipes/translate.pipe';
import { HasPermissionDirective } from 'app/shared/directives/has-permission.directive';
import { DevstroupeConfirmationService } from '@devstroupe/services/confirmation';

export interface CreateGroupDialogData {
    companyId: number;
    contacts: ConversationContact[];
    mode?: 'create' | 'edit';
    conversation?: Conversation | null;
}

export type CreateGroupDialogResult = Conversation | { deleted: true; conversation_id: number };

interface RoleGroup {
    roleKey: string;
    roleLabel: string;
    contacts: ConversationContact[];
}

interface CompanyGroup {
    companyKey: string;
    companyName: string;
    roles: RoleGroup[];
}

const PROFILE_LABELS: Record<string, string> = {
    DOCTOR: 'Médico',
    NURSE: 'Enfermeiro',
    NURSING_TECH: 'Técnico de Enfermagem',
    DRIVER: 'Motorista',
    PARAMEDIC: 'Socorrista',
    COMPANY_ADMIN: 'Administrativo',
    ADMIN: 'Admin',
    PATIENT: 'Paciente',
};

@Component({
    selector: 'app-create-group-dialog',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatDialogModule,
        MatButtonModule,
        MatCheckboxModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        AutoTranslateDirective,
        AutoTranslatePipe,
        HasPermissionDirective,
    ],
    templateUrl: './create-group-dialog.component.html',
    styleUrls: ['./create-group-dialog.component.scss'],
})
export class CreateGroupDialogComponent {
    name = '';
    search = '';
    saving = false;
    deleting = false;
    selected = new Set<number>();
    readonly isEdit: boolean;

    /** Evita recriar a árvore de contatos a cada CD (quebrava mat-checkbox). */
    private groupedContactsCache: CompanyGroup[] = [];
    private groupedContactsSearchKey = '';
    private groupedContactsSource: ConversationContact[] | null = null;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: CreateGroupDialogData,
        private readonly dialogRef: MatDialogRef<CreateGroupDialogComponent, CreateGroupDialogResult>,
        private readonly conversationService: ConversationService,
        private readonly confirmationService: DevstroupeConfirmationService,
    ) {
        this.isEdit = data.mode === 'edit' && !!data.conversation?.conversation_id;
        if (this.isEdit && data.conversation) {
            this.name = data.conversation.name || data.conversation.display_name || '';
            for (const p of data.conversation.participants || []) {
                if (p?.user_id) this.selected.add(p.user_id);
            }
        }
    }

    get selectedCount(): number {
        return this.selected.size;
    }

    get groupedContacts(): CompanyGroup[] {
        const query = this.search.trim().toLocaleLowerCase();
        const contactsRef = this.data.contacts ?? null;
        if (query !== this.groupedContactsSearchKey || contactsRef !== this.groupedContactsSource) {
            this.groupedContactsSearchKey = query;
            this.groupedContactsSource = contactsRef;
            const contacts = (contactsRef || []).filter((contact) => {
                if (!query) return true;
                const role = this.profileLabel(contact.profile_type, contact.role_name).toLocaleLowerCase();
                return (
                    contact.name.toLocaleLowerCase().includes(query) ||
                    (contact.company_name || '').toLocaleLowerCase().includes(query) ||
                    role.includes(query)
                );
            });
            this.groupedContactsCache = this.groupContacts(contacts);
        }
        return this.groupedContactsCache;
    }

    trackCompany(_index: number, company: CompanyGroup): string {
        return company.companyKey;
    }

    trackRole(_index: number, role: RoleGroup): string {
        return role.roleKey;
    }

    trackContact(_index: number, contact: ConversationContact): number {
        return contact.user_id;
    }

    profileLabel(profileType: string | null | undefined, roleName?: string | null): string {
        if (profileType && PROFILE_LABELS[profileType]) return PROFILE_LABELS[profileType];
        if (roleName?.trim()) return roleName.trim();
        if (profileType) return profileType;
        return 'Sem função';
    }

    getInitials(name: string | undefined | null): string {
        if (name == null || !String(name).trim()) return '?';
        const s = String(name).trim();
        const parts = s.split(/\s+/).filter(Boolean);
        if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        return s.slice(0, 2).toUpperCase();
    }

    isSelected(userId: number): boolean {
        return this.selected.has(userId);
    }

    roleSelectedCount(role: RoleGroup): number {
        return role.contacts.filter((c) => this.selected.has(c.user_id)).length;
    }

    isRoleFullySelected(role: RoleGroup): boolean {
        return role.contacts.length > 0 && role.contacts.every((c) => this.selected.has(c.user_id));
    }

    isRolePartiallySelected(role: RoleGroup): boolean {
        const count = this.roleSelectedCount(role);
        return count > 0 && count < role.contacts.length;
    }

    toggle(userId: number, checked: boolean): void {
        if (checked) this.selected.add(userId);
        else this.selected.delete(userId);
    }

    toggleRole(role: RoleGroup, checked: boolean): void {
        for (const contact of role.contacts) {
            this.toggle(contact.user_id, checked);
        }
    }

    save(): void {
        if (!this.name.trim() || this.saving || this.deleting) return;
        this.saving = true;
        const name = this.name.trim();
        const participantIds = [...this.selected];

        const request$ = this.isEdit
            ? this.conversationService.updateGroup(this.data.conversation!.conversation_id, {
                  name,
                  participant_ids: participantIds,
              })
            : this.conversationService.createGroup(this.data.companyId, name, participantIds);

        request$.subscribe({
            next: (conv) => this.dialogRef.close(conv),
            error: () => {
                this.saving = false;
            },
        });
    }

    confirmDelete(): void {
        if (!this.isEdit || !this.data.conversation || this.saving || this.deleting) return;

        const conversation = this.data.conversation;
        const label =
            conversation.type === 'SERVICE' && conversation.service_id
                ? `#${conversation.service_id}`
                : conversation.display_name || conversation.name || this.name || 'este grupo';

        const confirmation = this.confirmationService.open({
            title: 'Excluir grupo',
            message: `Tem certeza que deseja <strong>excluir</strong> ${label}?`,
            icon: {
                show: true,
                name: 'heroicons_outline:exclamation-triangle',
                color: 'warn',
            },
            actions: {
                confirm: {
                    show: true,
                    label: 'Excluir',
                    color: 'warn',
                },
                cancel: {
                    show: true,
                    label: 'Cancelar',
                },
            },
            dismissible: true,
        });

        confirmation.afterClosed().subscribe((result) => {
            if (result !== 'confirmed') return;
            this.deleting = true;
            this.conversationService.deleteConversation(conversation.conversation_id).subscribe({
                next: () =>
                    this.dialogRef.close({
                        deleted: true,
                        conversation_id: conversation.conversation_id,
                    }),
                error: () => {
                    this.deleting = false;
                },
            });
        });
    }

    private groupContacts(contacts: ConversationContact[]): CompanyGroup[] {
        const companies = new Map<string, { name: string; roles: Map<string, ConversationContact[]> }>();

        for (const contact of contacts) {
            const companyKey = contact.company_id != null ? String(contact.company_id) : 'none';
            const companyName = contact.company_name?.trim() || '';
            const roleKey = contact.profile_type || contact.role_name || 'none';
            if (!companies.has(companyKey)) {
                companies.set(companyKey, { name: companyName, roles: new Map() });
            }
            const company = companies.get(companyKey)!;
            if (!company.roles.has(roleKey)) {
                company.roles.set(roleKey, []);
            }
            company.roles.get(roleKey)!.push(contact);
        }

        return [...companies.entries()]
            .map(([companyKey, company]) => ({
                companyKey,
                companyName: company.name,
                roles: [...company.roles.entries()]
                    .map(([roleKey, roleContacts]) => ({
                        roleKey,
                        roleLabel: this.profileLabel(roleKey === 'none' ? null : roleKey, roleContacts[0]?.role_name),
                        contacts: roleContacts.slice().sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
                    }))
                    .sort((a, b) => a.roleLabel.localeCompare(b.roleLabel, 'pt-BR')),
            }))
            .sort((a, b) => a.companyName.localeCompare(b.companyName, 'pt-BR'));
    }
}
